import React from 'react';
import { z } from 'zod';
import {
  Card,
  Container,
  Row,
  Col,
  Form,
  HeaderText,
  BodyText,
  TextIconButton,
  ArrowLeftIcon,
  RotateIcon,
} from 'shared-portal-component';

type AuthMethod = 'sms' | 'email';
const CODE_LENGTH = 6;

const schema = z.object({
  code: z
    .string()
    .length(CODE_LENGTH, `Code must be exactly ${CODE_LENGTH} digits`)
    .regex(/^\d+$/, 'Code must contain only digits'),
});

interface AuthCodeProps {
  method: AuthMethod;
  onBack: () => void;
  onResend: () => void;
  onSubmit: (code: string) => void;
  loading?: boolean;
  error?: string;
  masked: string;
}

const AuthCode: React.FC<AuthCodeProps> = ({
  method,
  onBack,
  onResend,
  onSubmit,
  loading,
  masked,
  error,
}) => {
  const label =
    method === 'sms'
      ? `We’ve sent a 6-digit code to your phone number ending in ${masked}.`
      : `We’ve sent a 6-digit code to your email address ${masked}.`;

  const fields = [
    {
      name: 'code',
      label: 'Code',
      placeholder: 'Enter the 6-digit code',
      required: true,
      variant: 'outline' as const,
      type: 'code',
      numValues: CODE_LENGTH,
      display: (
        <Row spacing="none" justify="between">
          <TextIconButton
            icon={<ArrowLeftIcon />}
            text="Back"
            variant="outline"
            size="sm"
            onClick={onBack}
            type="button"
          />
          <TextIconButton
            icon={<RotateIcon />}
            text="Resend"
            variant="outline"
            size="sm"
            onClick={onResend}
            type="button"
            disabled={loading}
          />
        </Row>
      ),
    },
  ];

  return (
    <Container fluid>
      <Card>
        <Row spacing="md">
          <Col size={12}>
            <HeaderText
              text="Choose your second authentication method"
              className="header-text-auth"
            />
            <BodyText className="body-text-auth" as="p">
              {label} Please enter it below or, if you haven’t received it, click the Resend link.
            </BodyText>
          </Col>
        </Row>
        <Row justify="center" spacing="none">
          <Form
            schema={schema}
            fields={fields}
            onSubmit={({ code }) => {
              onSubmit(code);
            }}
            apiError={error}
          />
        </Row>
      </Card>
    </Container>
  );
};

export default AuthCode;
